import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertCircle, RefreshCw, Search } from 'lucide-react';
import { PageHeader } from '@/components/layout/page-header';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { FilterBar, type FilterField } from '@/components/ui/filter-bar';
import { Input } from '@/components/ui/input';
import { StatTile } from '@/components/ui/stat-tile';
import { SortableTable } from '@/components/ui/sortable-table';
import { formatDateTime, formatNumber } from '@/lib/format';
import {
  useAllPosLanes,
  versionStateOf,
  type PosLane,
  type VersionState,
} from './admin-pos.api';

/**
 * Every till on the platform, and which build it is running.
 *
 * The original listed the lanes and coloured the version green or red against
 * `latestVersion`, which is the useful part — but you could not *find* the red
 * ones without reading every row, and a lane that had never reported a version
 * at all got the same neutral tag as one that had. Both are filters here, and
 * the count of stale lanes leads the page, because that is the reason to open
 * it.
 */

const ANY = 'ANY';

const STATE_TONE: Record<VersionState, 'success' | 'destructive' | 'secondary'> =
  {
    current: 'success',
    behind: 'destructive',
    unknown: 'secondary',
  };

export function AdminPosLanesPage() {
  const { t } = useTranslation();

  const [term, setTerm] = useState('');
  const [state, setState] = useState(ANY);
  const [store, setStore] = useState(ANY);
  const [blocked, setBlocked] = useState(ANY);

  const query = useAllPosLanes();
  const latestVersion = query.data?.latestVersion ?? null;
  const lanes = useMemo(() => query.data?.lanes ?? [], [query.data]);

  const counts = useMemo(() => {
    const tally: Record<VersionState, number> = {
      current: 0,
      behind: 0,
      unknown: 0,
    };
    for (const lane of lanes) tally[versionStateOf(lane, latestVersion)] += 1;
    return tally;
  }, [lanes, latestVersion]);

  /** Store names come inline on each lane, so the filter is built from them. */
  const stores = useMemo(() => {
    const names = new Map<string, string>();
    for (const lane of lanes) {
      if (lane.businessId && lane.businessName) {
        names.set(lane.businessId, lane.businessName);
      }
    }
    return [...names.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  }, [lanes]);

  const needle = term.trim().toLowerCase();
  const rows = useMemo(
    () =>
      lanes.filter((lane) => {
        if (state !== ANY && versionStateOf(lane, latestVersion) !== state) {
          return false;
        }
        if (store !== ANY && lane.businessId !== store) return false;
        if (blocked !== ANY && String(lane.isBlocked) !== blocked) return false;
        if (!needle) return true;
        return [
          lane.tag,
          lane.businessName,
          lane.lastReportedDeviceName,
          lane.lastReportedDeviceModel,
          lane.lastReportedTerminalId,
        ]
          .filter(Boolean)
          .some((value) => value!.toLowerCase().includes(needle));
      }),
    [lanes, state, store, blocked, needle, latestVersion]
  );

  const fields: FilterField[] = [
    {
      id: 'state',
      label: t('adminPos.build'),
      value: state,
      onChange: setState,
      sort: false,
      resetValue: ANY,
      options: [
        { value: ANY, label: t('adminPos.anyBuild') },
        {
          value: 'behind',
          label: `${t('adminPos.behind')} (${counts.behind})`,
        },
        {
          value: 'current',
          label: `${t('adminPos.current')} (${counts.current})`,
        },
        {
          value: 'unknown',
          label: `${t('adminPos.unknown')} (${counts.unknown})`,
        },
      ],
    },
    {
      id: 'store',
      label: t('adminPos.store'),
      value: store,
      onChange: setStore,
      options: [
        { value: ANY, label: t('adminPos.allStores') },
        ...stores.map(([id, name]) => ({ value: id, label: name })),
      ],
    },
    {
      id: 'blocked',
      label: t('adminPos.access'),
      value: blocked,
      onChange: setBlocked,
      sort: false,
      resetValue: ANY,
      options: [
        { value: ANY, label: t('adminPos.anyAccess') },
        { value: 'false', label: t('adminPos.allowed') },
        { value: 'true', label: t('adminPos.blocked') },
      ],
    },
  ];

  const device = (lane: PosLane) =>
    [
      lane.lastReportedDeviceManufacturer,
      lane.lastReportedDeviceModel,
    ]
      .filter(Boolean)
      .join(' ') || '—';

  const VersionCell = ({ lane }: { lane: PosLane }) => {
    const value = versionStateOf(lane, latestVersion);
    return (
      <Badge variant={STATE_TONE[value]}>
        {lane.lastReportedVersion ?? t('adminPos.neverReported')}
      </Badge>
    );
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('adminPos.title')}
        description={t('adminPos.subtitle')}
        actions={
          <Button
            variant="outline"
            size="icon-sm"
            aria-label={t('common.reload')}
            disabled={query.isFetching}
            onClick={() => void query.refetch()}
          >
            <RefreshCw className={query.isFetching ? 'animate-spin' : ''} />
          </Button>
        }
      />

      <div className="grid grid-cols-2 gap-2 lg:grid-cols-3">
        <StatTile
          label={t('adminPos.latestBuild')}
          value={latestVersion ?? '—'}
          sub={t('adminPos.latestBuildSub')}
          loading={query.isPending}
          busy={query.isFetching}
        />
        <StatTile
          label={t('adminPos.behind')}
          value={formatNumber(counts.behind)}
          // The one figure on the page that is a problem when it is not zero.
          tone={counts.behind > 0 ? 'bad' : 'good'}
          sub={t('adminPos.behindSub', { total: lanes.length })}
          loading={query.isPending}
          busy={query.isFetching}
        />
        <StatTile
          label={t('adminPos.totalLanes')}
          value={formatNumber(lanes.length)}
          sub={t('adminPos.neverReportedSub', { count: counts.unknown })}
          loading={query.isPending}
          busy={query.isFetching}
        />
      </div>

      <FilterBar
        busy={query.isFetching}
        search={
          <div className="relative flex-1">
            <Search className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2" />
            <Input
              value={term}
              onChange={(event) => setTerm(event.target.value)}
              placeholder={t('adminPos.searchPlaceholder')}
              aria-label={t('adminPos.searchPlaceholder')}
              className="h-10 pl-9"
            />
          </div>
        }
        fields={fields}
        summary={
          query.isSuccess ? (
            <p className="text-muted-foreground text-sm" aria-live="polite">
              {t('adminPos.showingOf', {
                shown: rows.length,
                total: lanes.length,
              })}
            </p>
          ) : null
        }
      />

      {query.isError && (
        <div className="border-destructive/30 bg-destructive/8 text-destructive flex items-start gap-2 rounded-xl border p-4 text-sm">
          <AlertCircle className="mt-0.5 size-4 shrink-0" />
          <div className="space-y-2">
            <p>{t('adminPos.loadFailed')}</p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => void query.refetch()}
            >
              {t('common.retry')}
            </Button>
          </div>
        </div>
      )}

      <SortableTable
        rows={rows}
        rowKey={(lane) => lane.id}
        loading={query.isPending}
        busy={query.isFetching}
        pageSize={25}
        emptyMessage={
          lanes.length === 0 ? t('adminPos.none') : t('adminPos.noneMatch')
        }
        // Opens on the stalest builds, which is the reason to be here. Sorting
        // by version is a string sort, so it groups builds rather than ranking
        // them — good enough to gather the outliers, and honest about it.
        initialSort={{ key: 'version', direction: 'asc' }}
        columns={[
          {
            key: 'store',
            header: t('adminPos.store'),
            sortValue: (lane) => lane.businessName ?? '',
            render: (lane) => (
              <span className="truncate">{lane.businessName || '—'}</span>
            ),
          },
          {
            key: 'lane',
            header: t('adminPos.lane'),
            sortValue: (lane) => lane.tag ?? '',
            render: (lane) => (
              <div className="min-w-0">
                <p className="font-medium">{lane.tag || '—'}</p>
                {lane.lastReportedTerminalId && (
                  <p className="text-muted-foreground font-mono text-xs">
                    {lane.lastReportedTerminalId}
                  </p>
                )}
              </div>
            ),
          },
          {
            key: 'version',
            header: t('adminPos.version'),
            sortValue: (lane) => lane.lastReportedVersion ?? '',
            render: (lane) => <VersionCell lane={lane} />,
          },
          {
            key: 'device',
            header: t('adminPos.device'),
            sortValue: (lane) => device(lane),
            render: (lane) => (
              <div className="text-xs">
                <p>{device(lane)}</p>
                {lane.lastReportedDeviceName && (
                  <p className="text-muted-foreground">
                    {lane.lastReportedDeviceName}
                  </p>
                )}
              </div>
            ),
          },
          {
            key: 'lastTransaction',
            header: t('adminPos.lastTransaction'),
            // Never-sold lanes sort together at one end rather than scattering
            // among the dates, which an empty string would do.
            sortValue: (lane) =>
              lane.lastTransactionOn ? Date.parse(lane.lastTransactionOn) : 0,
            render: (lane) => (
              <span className="whitespace-nowrap">
                {lane.lastTransactionOn
                  ? formatDateTime(lane.lastTransactionOn)
                  : '—'}
              </span>
            ),
          },
          {
            key: 'access',
            header: t('adminPos.access'),
            sortValue: (lane) => (lane.isBlocked ? 0 : 1),
            render: (lane) =>
              lane.isBlocked ? (
                <Badge variant="destructive">{t('adminPos.blocked')}</Badge>
              ) : (
                <Badge variant="secondary">{t('adminPos.allowed')}</Badge>
              ),
          },
        ]}
      />
    </div>
  );
}
